import { useThemeContext } from '#contexts/ThemeContext';
import { InfoHelperIcon } from '#images/icon';
import { Answer, QuestionnaireElements } from '#models/questionnaire';
import { useAnswerStyles } from '#styles/imageAnswer';
import { Box, Checkbox, makeStyles, Paper, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { CheckOutlined, CloseOutlined } from '@material-ui/icons';
import * as React from 'react';

interface LargeMultipleAnswerProps {
  answer: Answer;
  showHelperText: any;
  selected?: boolean;
  multipleSelection?: boolean;
}

const useStyles = (
  { answerCardSelectedState, helperIconLink, isTouchDevice }: Partial<QuestionnaireElements>,
  isSmDown: Boolean,
  multipleSelection: Boolean
) =>
  makeStyles((theme: any) => ({
    helperIconContainer: {
      position: 'absolute',
      padding: 0,
      top: 5,
      right: 5
    },
    icon: {
      color: helperIconLink
    },
    getFixedSize: {
      [theme.breakpoints.up('md')]: {
        width: 171,
        height: multipleSelection ? 221 : 201
      },
      [theme.breakpoints.up('lg')]: {
        width: 254,
        height: multipleSelection ? 260 : 250
      }
    },
    answerBox: {
      outline: '1px solid transparent',
      cursor: 'pointer',
      '&:hover': {
        outlineColor: isTouchDevice || isSmDown ? 'transparent' : answerCardSelectedState + '!important'
      }
    },
    answerBoxSelected: {
      outlineColor: answerCardSelectedState + '!important'
    }
  }));

export const LargeMultipleAnswer = ({ answer, selected, multipleSelection }: LargeMultipleAnswerProps) => {
  const themeMaterial = useTheme();
  const isSmDown = useMediaQuery(themeMaterial.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(themeMaterial.breakpoints.down('md'));
  const theme = useThemeContext();
  const classes = useStyles(theme, isSmDown, !!multipleSelection)();
  const [isShowHelperText, setIsShowHelperText] = React.useState<boolean>(false);
  const classCard = useAnswerStyles(isShowHelperText)();

  const handleClickAnswer = (e: React.MouseEvent) => {
    if (isShowHelperText) {
      e.stopPropagation();
    }
  };

  return (
    <Paper className={`${classes.getFixedSize} ${classes.answerBox} ${selected ? classes.answerBoxSelected : ''}`}>
      <Box
        position='relative'
        height='100%'
        display='flex'
        flexDirection='column'
        onClick={(e) => handleClickAnswer(e)}
      >
        {isShowHelperText && (
          <Box
            onClick={() => setIsShowHelperText(false)}
            bgcolor={theme.primaryButtonBackground}
            width={40}
            height={40}
            position='absolute'
            display='flex'
            alignItems='center'
            justifyContent='center'
            top={-1}
            right={-1}
            borderRadius={'0 0 0 10px'}
          >
            <CloseOutlined style={{ color: theme.primaryButtonText }} />
          </Box>
        )}
        {isShowHelperText && <Box height={60} width='100%' />}
        <Box
          px={2}
          display='flex'
          flexDirection='column'
          justifyContent={'center'}
          alignItems='center'
          height={isShowHelperText ? 'auto' : '100%'}
        >
          <Box display='flex' justifyContent='center' alignItems='center' height={'calc(100% / 3)'} width='100%'>
            <Typography
              variant={isShowHelperText ? (isMdDown ? 'subtitle2' : 'subtitle1') : isMdDown ? 'subtitle1' : 'h6'}
              className={classCard.answerText}
              align='center'
            >
              {answer.text}
            </Typography>
          </Box>
          {multipleSelection && !isShowHelperText && (
            <Box mt={{ xs: 1, lg: 3 }}>
              <Checkbox
                color='primary'
                checked={selected}
                style={{ padding: '0px 0.625rem' }}
                className={classCard.checkbox}
                checkedIcon={<CheckOutlined />}
              />
            </Box>
          )}
        </Box>
        {answer.helperText && !isShowHelperText && (
          <Box
            height={58}
            width='100%'
            display='flex'
            alignItems='center'
            justifyContent='center'
            onClick={(e) => {
              e.stopPropagation();
              setIsShowHelperText(true);
            }}
            className={classCard.helperAnswerWrapper}
            position='absolute'
            bottom={0}
            pb={2}
          >
            <InfoHelperIcon className='helperIcon' />
          </Box>
        )}
        {answer.helperText && isShowHelperText && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={classCard.helperAnswerWrapper}
            overflow='auto'
            p={2}
            pb={0}
            mb={isShowHelperText ? 2 : 0}
            height={150}
          >
            <Typography variant={isMdDown ? 'caption' : 'subtitle2'} style={{ color: theme.answerHelperText }}>
              {answer.helperText}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
